import { Component, ElementRef, Input, ViewChild } from '@angular/core';
import { ChatRoomMessagesService } from '../_services/chat-room-messages.service';
import { ChatRoomsService } from '../_services/chat-rooms.service';
import { IParamsCollection } from 'src/app/core/jsonapi/interfaces/params-collection';
import { ChatRoom } from '../_types/chat-room';
import { FilterBuilder } from 'src/app/core/jsonapi/filters-builder';
import { Filter, FilterOperation } from 'src/app/core/jsonapi/filter';
import { ChatRoomMessage } from '../_types/chat-room-message';
import { firstValueFrom } from 'rxjs';
import { User } from '../../users/_types/user';
import { LocalStorageService } from 'angular-web-storage';
import { IMqttMessage, MqttService } from 'ngx-mqtt';
import { ChatService } from '../_services/chat.service';
import { CaseService } from '../../cases/_types/case-service';
import { CasesServicesService } from '../../cases/_services/cases-services.service';
import { ChatRoomUsersService } from '../_services/chat-room-users.service';
import { Case } from '../../cases/_types/case';
import { CasesService } from '../../cases/_services/cases.service';
import { NotificationsService } from '../../notifications/_services/notifications.service';

@Component({
    selector: 'app-chat-detail',
    templateUrl: './chat-detail.component.html',
    styleUrls: ['./chat-detail.component.scss'],
    standalone: false
})
export class ChatDetailComponent {
  @Input() public code!: string;
  public user!:User;
  public title: string;
  public message:string = "";
  public isImportant:boolean = false;
  public chatRoom!:ChatRoom;
  public messages:ChatRoomMessage[] = [];
  public case?:Case;
  @ViewChild('chatMessages') public chatMessages!:ElementRef;

  private keepAliveTimeout:any;
  private caseServiceValue!:CaseService;

  constructor(
    private chatService:ChatService,
    private chatRoomService: ChatRoomsService,
    private chatRoomMessageService: ChatRoomMessagesService,
    private storage:LocalStorageService,
    private mqttService:MqttService,
    private caseServiceService: CasesServicesService,
    private chatRoomUsersService: ChatRoomUsersService,
    private caseService: CasesService,
    private notificationsService: NotificationsService
  ) {
    this.title = '';
    this.chatService.onCloseChat.subscribe(() => {
      clearTimeout(this.keepAliveTimeout);
      this.disconnect();
    });
  }

  async ngOnInit() {
    if(this.code) this.loadChatRoom(this.code);
  }

  async loadChatRoom(code:string, title:string|null=null){
    this.code = code;
    this.title = title??'Chat';
    this.user = this.storage.get('user');
    let filters = {
      code: new Filter(FilterOperation.equals, 'code', "", code)
    };
    let params:IParamsCollection = {
      filter: FilterBuilder.fromObject(filters).build()
    };
    let response = await firstValueFrom(this.chatRoomService.all(params));
    if(response.data.length>0){
      this.chatRoom = response.data[0];
      if(title!=null && this.chatRoom.title != title){
        //si ha cambiado el titulo, lo actualizamos
        this.chatRoom.title = this.title;
        await firstValueFrom(this.chatRoomService.save(this.chatRoom));
      }
    }else{
      //si no existe, se crea la sala
      let newChatRoom = new ChatRoom();
      newChatRoom.code = code;
      newChatRoom.title = this.title;
      this.chatRoom = await firstValueFrom(this.chatRoomService.save(newChatRoom));
      
    }
    
    //cargamos info adicional
    if(this.chatRoom!=null){
      this.keepAlive();
      this.loadMessages();
      this.loadCase();
    }

    //subscribimos a topic
    this.mqttService.observe('chat/'+this.chatRoom.id).subscribe((data:IMqttMessage) => {
      let message:ChatRoomMessage = JSON.parse(data.payload.toString());
      this.addMessage(message);
    });

  }

  async keepAlive(){
    this.chatRoomService.Keepalive(this.chatRoom).subscribe((response) => {
      this.keepAliveTimeout = setTimeout(() => { this.keepAlive(); }, 10 * 1000);
    }, (error) => {
      this.keepAliveTimeout = setTimeout(() => { this.keepAlive(); }, 10 * 1000);
    });
  }

  
  async disconnect(){
    this.chatRoomService.Disconnect(this.chatRoom).subscribe((response) => {
      //console.log(response);
    });
  } 
    

  //marca la sala como leida bajo demanda, igual que al enviar un mensaje: el usuario y, si es tramitador,
  //el resto de tramitadores de la sala.
  async markAsRead(){
    await firstValueFrom(this.chatRoomService.MarkAsRead(this.chatRoom));
    this.storage.remove("chatsNotReaded");
  }

  async removeMe(){
    this.chatRoomService.RemoveMe(this.chatRoom).subscribe((response) => {
      //cerrar el chat
      this.chatService.closeChatRoom();
    });
  }

  async loadCase(){
    if(this.chatRoom.code.startsWith('case-service:')){
      let caseServiceId = this.chatRoom.code.split(':')[1];
      this.caseServiceValue = await firstValueFrom(this.caseServiceService.get(caseServiceId));
      this.case = await firstValueFrom(this.caseService.get(this.caseServiceValue.caseId, { include: ['client.address']}));
    }
  }

  async loadMessages(){
    let filters = {
      chatRoomId: new Filter(FilterOperation.equals, 'chatRoomId', "", this.chatRoom.id)
    };
    let params:IParamsCollection = {
      filter: FilterBuilder.fromObject(filters).build(),
      sort: ['createdAt'],
      include: ['createdByUser']
    };
    let response = await firstValueFrom(this.chatRoomMessageService.all(params));
    this.messages = response.data;
    this.scrollChatMessages();

    //Marcamos los mensajes como leídos.
    this.setNotificationsReaded();
  }

  onEnter(event: KeyboardEvent){
    if(event.isComposing || event.keyCode === 229) return; //Enter de confirmacion de IME, no enviar
    if(event.key !== 'Enter' || event.shiftKey) return; //shift+enter inserta salto de linea
    event.preventDefault();
    this.sendMessage();
  }

  async sendMessage(){
    if(!this.message?.trim()) return;

    let newMessage = new ChatRoomMessage();
    newMessage.chatRoomId = this.chatRoom.id!;
    newMessage.message = this.message.trim();
    newMessage.isImportant = this.isImportant;
    let response = await firstValueFrom(this.chatRoomMessageService.save(newMessage));

    this.isImportant = false;

    //enviamos al topic
    this.mqttService.publish('chat/'+this.chatRoom.id, JSON.stringify(response)).subscribe({
      next: () => {
      },
      error: (error) => {
        console.error('publish error: ', error);
      }
    });

    //vacia el mensaje
    this.message = "";

    //añadimos el mensaje a la lista
    this.addMessage(response);
  }

  async addMessage(message:ChatRoomMessage){
    //si el mensaje no esta en ventana, lo añadimos
    if(this.messages.find(m => m.id == message.id)==null) this.messages.push(message);
    this.scrollChatMessages();
  }

  async scrollChatMessages(){
    setTimeout(() => {
      this.chatMessages.nativeElement.scrollTop = this.chatMessages.nativeElement.scrollHeight;
    }, 300);
  }

  async setImportantMessage(message: any){
    message.isImportant = !message.isImportant;
    await firstValueFrom(this.chatRoomMessageService.save(message));

    //marcamos el caso con IsLocked
    this.caseServiceValue.isLocked = message.isImportant;
    await firstValueFrom(this.caseServiceService.save(this.caseServiceValue));
  
  }

  //buscamos si tiene una notificación de este chat sin leer, y se la marcamos como leída.
  async setNotificationsReaded(){
        let user = this.storage.get('user');

     let filters = {
      readedAt: new Filter(FilterOperation.equals, 'readedAt', '', null, true),
      web: new Filter(FilterOperation.equals, 'web', '', true),
      userId: new Filter(FilterOperation.equals, 'userId', '', user.id),
      source: new Filter(FilterOperation.equals, 'source', '', 'Chat')
    };
    let params:IParamsCollection = {
        page: { size: 1 },
        sort: ['createdAt'],
        filter: FilterBuilder.fromObject(filters).build()
    };
    this.notificationsService.all(params, true).subscribe((response:any) => {
      if(response){
        if(response.data.length > 0){
          response.data.forEach((notificationItem: any) => {
                let notification = notificationItem;
                if(!notification.readedAt) firstValueFrom(this.notificationsService.markAsReaded(notification));
          });
        }
      }
    },(error) => {
    
    });
        
  }

}
